import type {
  StateHistoryEntry,
  BulkAssignmentRequest,
  BulkAssignmentResponse,
  BulkNoInterestRequest,
  BulkNoInterestResponse,
  CatalogResponse,
  Category,
  CategorySummary,
  DashboardData,
  Employee,
  CreateEmployeeResponse,
  EmployeeCreate,
  EmployeeListResponse,
  EmployeeStats,
  EmployeeUpdate,
  ExecutionWithUnassignedProperties,
  OnOfficeSyncResponse,
  OnOfficeSyncStatusResponse,
  PaginatedPropertyResponse,
  PaginatedPropertyWithExecutionResponse,
  PropertiesWithCountResponse,
  Property,
  PropertyNote,
  PropertyNoteCreate,
  PropertyNoteUpdate,
  PropertyRefreshResponse,
  PropertyWithExecution,
  RevenueStatsResponse,
  Run,
  RunExecution,
  RunWithExecutions,
  RunWithUnassignedProperties,
  RunEstimate,
  RunEstimateRequest,
  ScraperInfo,
  SendRequest,
  SendResponse,
  StatisticResponse,
  GoalCreate,
  GoalUpdate,
  EmployeeGoal,
  GoalListResponse,
  GoalProgressResponse,
  GoalSummary,
  GoalDashboardResponse,
  ManualPropertyCreate,
  ManualPropertyUpdate,
  UploadedDocument,
  DocumentCreate,
  DocumentUpdate,
  DocumentListResponse,
  DocumentVariant,
  FieldConfig,
  DocumentBundleResponse,
  BundleCreate,
  BundleUpdate,
  BundleListResponse,
  CustomEmailTemplateResponse,
  EmailTemplateCreate,
  EmailTemplateUpdate,
  EmailTemplateListResponse,
  NoInterestStatsResponse,
} from "@/types";

export interface BlacklistItem {
  id: string;
  type: "phone_number" | "legal_name" | "keyword";
  value: string;
  description?: string;
  source?: string;
  created_at: string;
  updated_at?: string;
}

const RAW_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// Ensure HTTPS in production to prevent mixed content errors
const normalizeUrl = (url: string): string => {
  const cleanUrl = url.replace(/\/+$/, "");
  // In production (non-localhost), always use HTTPS
  if (!cleanUrl.includes("localhost") && !cleanUrl.includes("127.0.0.1")) {
    return cleanUrl.replace(/^http:/, "https:");
  }
  return cleanUrl;
};

export const API_BASE_URL = `${normalizeUrl(RAW_BASE_URL)}/api`;

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem("auth_token");
  } catch (error) {
    console.error("Error reading auth token from localStorage:", error);
    return null;
  }
}

function getRefreshToken(): string | null {
  try {
    return localStorage.getItem("refresh_token");
  } catch (error) {
    console.error("Error reading refresh token from localStorage:", error);
    return null;
  }
}

function setAuthToken(token: string): void {
  localStorage.setItem("auth_token", token);
}

function clearAuthTokens(): void {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("auth_user");
}

// Event to notify the app that user should be logged out
const authLogoutEvent = new CustomEvent("auth:logout");

function triggerLogout(): void {
  clearAuthTokens();
  window.dispatchEvent(authLogoutEvent);
}

export class ApiError extends Error {
  status: number;
  code?: string;
  /** The parsed error body, e.g. executionId of a 409 execution_in_flight */
  details?: Record<string, unknown>;

  constructor(message: string, status: number, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

class ApiClient {
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  private async refreshAccessToken(): Promise<boolean> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      if (data.access_token && data.refresh_token) {
        // Store both new access token and rotated refresh token
        setAuthToken(data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  private async handleTokenRefresh(): Promise<boolean> {
    // If already refreshing, wait for the existing refresh to complete
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.refreshAccessToken();

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit,
    isRetry = false,
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const authToken = getAuthToken();

    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...options?.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Handle 401 Unauthorized - try to refresh token
        if (
          response.status === 401 &&
          !isRetry &&
          !endpoint.includes("/auth/login") &&
          !endpoint.includes("/auth/refresh")
        ) {
          const refreshed = await this.handleTokenRefresh();
          if (refreshed) {
            // Retry the request with new token
            return this.request<T>(endpoint, options, true);
          } else {
            // Refresh failed, trigger logout
            triggerLogout();
            throw new ApiError(
              "Session expired. Please log in again.",
              401,
              "SESSION_EXPIRED",
            );
          }
        }

        let errorMessage = `HTTP ${response.status}`;
        let errorCode: string | undefined;
        let errorDetails: Record<string, unknown> | undefined;

        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.detail || errorMessage;
          errorCode = errorData.code;
          errorDetails = errorData;
        } catch {
          // If error response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }

        throw new ApiError(errorMessage, response.status, errorCode, errorDetails);
      }

      // Handle empty responses
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        return response.json();
      } else {
        return {} as T;
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Network errors, etc.
      throw new ApiError(
        error instanceof Error ? error.message : "Network error occurred",
        0,
      );
    }
  }

  private async requestBlob(
    endpoint: string,
    options?: RequestInit,
    isRetry = false,
  ): Promise<Blob> {
    const url = `${API_BASE_URL}${endpoint}`;
    const authToken = getAuthToken();

    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...options?.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        if (response.status === 401 && !isRetry) {
          const refreshed = await this.handleTokenRefresh();
          if (refreshed) {
            return this.requestBlob(endpoint, options, true);
          } else {
            triggerLogout();
            throw new ApiError(
              "Session expired. Please log in again.",
              401,
              "SESSION_EXPIRED",
            );
          }
        }

        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.detail || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new ApiError(errorMessage, response.status);
      }

      return response.blob();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : "Network error occurred",
        0,
      );
    }
  }

  // Dashboard methods
  async getDashboardStats(): Promise<DashboardData> {
    const [runs, health, scraperInfo, runningExecutionsList] =
      await Promise.allSettled([
        this.getRuns(),
        this.getHealth(),
        this.getScraperInfo(),
        this.getExecutions("running"),
      ]);

    const defaultScraperInfo: ScraperInfo = {
      brightdata_available: false,
      available_platforms: [],
      platform_fields: {},
      supported_transaction_types: [],
      test_endpoints: [],
      features: { multi_platform_support: false },
    };

    // Handle potential errors in parallel requests
    const runsData = runs.status === "fulfilled" ? runs.value : [];
    const healthData =
      health.status === "fulfilled" ? health.value : { status: "unknown" };
    const scraperData =
      scraperInfo.status === "fulfilled"
        ? scraperInfo.value
        : defaultScraperInfo;

    const totalExecutions = runsData.reduce(
      (sum, run) => sum + run.total_executions,
      0,
    );
    const totalProperties = runsData.reduce(
      (sum, run) => sum + run.total_properties_scraped,
      0,
    );
    const activeRuns = runsData.filter((run) => run.status === "active").length;
    // Count the executions themselves: run.last_execution_status is only ever
    // written as completed/failed, so it never says "running".
    const runningExecutions =
      runningExecutionsList.status === "fulfilled"
        ? runningExecutionsList.value.length
        : 0;

    return {
      system: healthData,
      scraper: scraperData,
      stats: {
        total_run_configurations: runsData.length,
        active_run_configurations: activeRuns,
        total_executions: totalExecutions,
        running_executions: runningExecutions,
        total_properties_scraped: totalProperties,
        platforms_available: scraperData.available_platforms?.length || 0,
      },
      recent_runs: runsData.slice(0, 5),
    };
  }

  // Run methods
  async getRuns(status?: string): Promise<Run[]> {
    const params = status ? `?status=${status}` : "";
    return this.request<Run[]>(`/runs/${params}`);
  }

  async estimateRun(config: RunEstimateRequest): Promise<RunEstimate> {
    return this.request<RunEstimate>("/runs/estimate", {
      method: "POST",
      body: JSON.stringify(config),
    });
  }

  async getRunsWithUnassignedProperties(): Promise<
    RunWithUnassignedProperties[]
  > {
    return this.request<RunWithUnassignedProperties[]>("/runs/unassigned");
  }

  async getRun(id: string): Promise<RunWithExecutions> {
    return this.request<RunWithExecutions>(`/runs/${id}`);
  }

  async createRun(data: Partial<Run>): Promise<Run> {
    return this.request<Run>("/runs/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateRun(id: string, data: Partial<Run>): Promise<Run> {
    return this.request<Run>(`/runs/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteRun(id: string): Promise<void> {
    return this.request<void>(`/runs/${id}`, {
      method: "DELETE",
    });
  }

  async executeRun(
    id: string,
    triggeredBy = "manual",
  ): Promise<{ execution_id: string; execution_number: number }> {
    return this.request<{ execution_id: string; execution_number: number }>(
      `/runs/${id}/execute`,
      {
        method: "POST",
        body: JSON.stringify({ triggered_by: triggeredBy }),
      },
    );
  }

  async toggleRunStatus(id: string): Promise<{ status: string }> {
    return this.request<{ status: string }>(`/runs/${id}/toggle-status`, {
      method: "POST",
    });
  }

  async getExecutions(status?: string): Promise<RunExecution[]> {
    const params = status ? `?status=${status}` : "";
    return this.request<RunExecution[]>(`/executions/${params}`);
  }

  async getExecutionsWithUnassignedProperties(): Promise<
    ExecutionWithUnassignedProperties[]
  > {
    return this.request<ExecutionWithUnassignedProperties[]>(
      "/executions/unassigned",
    );
  }

  async getExecutionsForRun(runId: string): Promise<RunExecution[]> {
    return this.request<RunExecution[]>(`/executions/run/${runId}`);
  }

  async getExecution(id: string): Promise<RunExecution> {
    return this.request<RunExecution>(`/executions/${id}`);
  }

  async stopExecution(id: string): Promise<RunExecution> {
    return this.request<RunExecution>(`/executions/${id}/stop`, {
      method: "POST",
    });
  }

  async getPropertiesWithExecutions(params?: {
    search?: string;
    assigned_to?: string;
    states?: string[];
    platform?: string;
    run_id?: string;
    execution_id?: string;
    skip?: number;
    limit?: number;
    has_unpaid_commissions?: boolean;
  }): Promise<PropertyWithExecution[]> {
    const results = await Promise.allSettled([
      this.getProperties(params),
      params?.execution_id
        ? this.getExecutionsForRun(params?.run_id || "")
        : this.getExecutionsWithUnassignedProperties(),
      params?.run_id
        ? this.getRun(params.run_id).then((r) => [r])
        : this.getRuns(),
    ]);

    const properties: Property[] =
      results[0].status == "fulfilled" ? results[0].value : [];

    const executions: RunExecution[] =
      results[1].status == "fulfilled" ? results[1].value : [];

    const runs: Run[] =
      results[2].status == "fulfilled" ? results[2].value : [];

    return properties.map((p) => {
      const exec = executions.find((e) => e.id === p.execution_id);
      const run = runs.find((r) => r.id === p.run_id);

      return {
        ...p,
        run_name: run?.name,
        run_status: run?.status,
        execution_number: exec?.execution_number,
        execution_status: exec?.status,
      };
    });
  }

  async getProperties(params?: {
    search?: string;
    assigned_to?: string;
    states?: string[];
    platform?: string;
    run_id?: string;
    execution_id?: string;
    skip?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
    service_type?: string;
    commission_employee_id?: string;
    completion_start_date?: string;
    completion_end_date?: string;
    has_unpaid_commissions?: boolean;
  }): Promise<Property[]> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.append("search", params.search);
    if (params?.assigned_to)
      searchParams.append("assigned_to", params.assigned_to);
    if (params?.states && params.states.length > 0) {
      params.states.forEach((state) => {
        searchParams.append("states", state);
      });
    }
    if (params?.platform) searchParams.append("platform", params.platform);
    if (params?.run_id) searchParams.append("run_id", params.run_id);
    if (params?.execution_id)
      searchParams.append("execution_id", params.execution_id);
    if (params?.skip !== undefined)
      searchParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      searchParams.append("limit", params.limit.toString());
    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);
    if (params?.service_type)
      searchParams.append("service_type", params.service_type);
    if (params?.commission_employee_id)
      searchParams.append(
        "commission_employee_id",
        params.commission_employee_id,
      );
    if (params?.completion_start_date)
      searchParams.append(
        "completion_start_date",
        params.completion_start_date,
      );
    if (params?.completion_end_date)
      searchParams.append("completion_end_date", params.completion_end_date);
    if (params?.has_unpaid_commissions !== undefined)
      searchParams.append(
        "has_unpaid_commissions",
        params.has_unpaid_commissions.toString(),
      );

    const query = searchParams.toString();
    return this.request<Property[]>(`/properties/${query ? `?${query}` : ""}`);
  }

  async getPropertiesCount(params?: {
    search?: string;
    assigned_to?: string;
    states?: string[];
    platform?: string;
    run_id?: string;
    execution_id?: string;
    start_date?: string;
    end_date?: string;
    service_type?: string;
    commission_employee_id?: string;
    completion_start_date?: string;
    completion_end_date?: string;
    has_unpaid_commissions?: boolean;
  }): Promise<number> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.append("search", params.search);
    if (params?.assigned_to)
      searchParams.append("assigned_to", params.assigned_to);
    if (params?.states && params.states.length > 0) {
      params.states.forEach((state) => {
        searchParams.append("states", state);
      });
    }
    if (params?.platform) searchParams.append("platform", params.platform);
    if (params?.run_id) searchParams.append("run_id", params.run_id);
    if (params?.execution_id)
      searchParams.append("execution_id", params.execution_id);
    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);
    if (params?.service_type)
      searchParams.append("service_type", params.service_type);
    if (params?.commission_employee_id)
      searchParams.append(
        "commission_employee_id",
        params.commission_employee_id,
      );
    if (params?.completion_start_date)
      searchParams.append(
        "completion_start_date",
        params.completion_start_date,
      );
    if (params?.completion_end_date)
      searchParams.append("completion_end_date", params.completion_end_date);
    if (params?.has_unpaid_commissions !== undefined)
      searchParams.append(
        "has_unpaid_commissions",
        params.has_unpaid_commissions.toString(),
      );

    const query = searchParams.toString();
    return this.request<number>(`/properties/count${query ? `?${query}` : ""}`);
  }

  async getDeduplicationStats(): Promise<{
    total_properties: number;
    unique_listing_ids: number;
    duplicate_count: number;
    deduplication_rate: number;
    redis_cache_hits: number;
    redis_cache_misses: number;
    cache_hit_rate: number;
  }> {
    return this.request<{
      total_properties: number;
      unique_listing_ids: number;
      duplicate_count: number;
      deduplication_rate: number;
      redis_cache_hits: number;
      redis_cache_misses: number;
      cache_hit_rate: number;
    }>("/properties/deduplication/stats");
  }

  async getPropertiesForExecution(executionId: string): Promise<Property[]> {
    return this.request<Property[]>(`/executions/${executionId}/properties`);
  }

  async createManualProperty(data: ManualPropertyCreate): Promise<Property> {
    return this.request<Property>("/properties/manual", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateManualProperty(
    id: string,
    data: ManualPropertyUpdate,
  ): Promise<Property> {
    return this.request<Property>(`/properties/${id}/manual`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async updatePropertyContact(
    id: string,
    data: { contact_name?: string | null; email?: string | null },
  ): Promise<Property> {
    return this.request<Property>(`/properties/${id}/contact`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteProperty(id: string): Promise<void> {
    return this.request<void>(`/properties/${id}`, {
      method: "DELETE",
    });
  }

  async sendPropertyToOnOffice(id: string): Promise<OnOfficeSyncResponse> {
    return this.request<OnOfficeSyncResponse>(
      `/properties/${id}/send-to-onoffice`,
      {
        method: "POST",
      },
    );
  }

  async getOnOfficeSyncStatus(id: string): Promise<OnOfficeSyncStatusResponse> {
    return this.request<OnOfficeSyncStatusResponse>(
      `/properties/${id}/onoffice-status`,
    );
  }

  async refreshProperty(id: string): Promise<PropertyRefreshResponse> {
    return this.request<PropertyRefreshResponse>(`/properties/${id}/refresh`, {
      method: "POST",
    });
  }

  async assignPropertiesBulkToEmployee(
    requestBody: BulkAssignmentRequest,
  ): Promise<BulkAssignmentResponse> {
    return this.request<BulkAssignmentResponse>(`/properties/bulk/assign`, {
      method: "PUT",
      body: JSON.stringify(requestBody),
    });
  }

  async setPropertiesBulkNoInterest(
    requestBody: BulkNoInterestRequest,
  ): Promise<BulkNoInterestResponse> {
    return this.request<BulkNoInterestResponse>(`/properties/bulk/no-interest`, {
      method: "POST",
      body: JSON.stringify(requestBody),
    });
  }

  async assignPropertyToEmployee(
    id: string,
    employeeId: string | null,
  ): Promise<Property> {
    return this.request<Property>(`/properties/${id}/assign`, {
      method: "PUT",
      body: JSON.stringify({ assigned_to: employeeId }),
    });
  }

  async getUnassignedProperties(params?: {
    skip?: number;
    limit?: number;
    name?: string;
    transaction_type?: string;
    categories?: string[];
    priceMin?: string;
    priceMax?: string;
    runId?: string;
    executionId?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<PaginatedPropertyWithExecutionResponse> {
    const searchParams = new URLSearchParams();

    if (params?.skip !== undefined)
      searchParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      searchParams.append("limit", params.limit.toString());
    if (params?.name) searchParams.append("name", params.name);
    if (params?.transaction_type)
      searchParams.append("transaction_type", params.transaction_type);
    if (params?.categories && params.categories.length > 0) {
      params.categories.forEach((category) => {
        searchParams.append("categories", category);
      });
    }
    if (params?.priceMin) searchParams.append("price_min", params.priceMin);
    if (params?.priceMax) searchParams.append("price_max", params.priceMax);
    if (params?.sortBy) searchParams.append("sort_by", params.sortBy);
    if (params?.sortOrder) searchParams.append("sort_order", params.sortOrder);

    if (params?.runId) searchParams.append("run_id", params.runId);
    if (params?.executionId)
      searchParams.append("execution_id", params.executionId);

    const query = searchParams.toString();
    const results = await Promise.allSettled([
      this.request<PaginatedPropertyResponse>(
        `/properties/unassigned${query ? `?${query}` : ""}`,
      ),
      params?.executionId
        ? this.getExecutionsForRun(params?.runId || "")
        : this.getExecutions(),
      params?.runId
        ? this.getRun(params?.runId).then((r) => [r])
        : this.getRuns(),
    ]);

    const paginatedResponse: PaginatedPropertyResponse =
      results[0].status === "fulfilled"
        ? results[0].value
        : { properties: [], total: 0, skip: 0, limit: 0, has_more: false };

    const executions: RunExecution[] =
      results[1].status == "fulfilled" ? results[1].value : [];

    const runs: Run[] =
      results[2].status == "fulfilled" ? results[2].value : [];

    const propertiesWithExecution: PropertyWithExecution[] =
      paginatedResponse.properties.map((p) => {
        const exec = executions.find((e) => e.id === p.execution_id);
        const run = runs.find((r) => r.id === p.run_id);

        return {
          ...p,
          execution_number: exec?.execution_number,
          execution_status: exec?.status,
          run_name: run?.name,
          run_status: run?.status,
        };
      });

    return {
      ...paginatedResponse,
      properties: propertiesWithExecution,
    };
  }

  getUnassignedPropertyCategories(): Promise<string[]> {
    return this.request<string[]>("/properties/unassigned/categories");
  }

  async getProperty(id: string): Promise<PropertyWithExecution> {
    return this.request<PropertyWithExecution>(`/properties/${id}`);
  }

  async getPropertiesByPhoneNumber(
    phoneNumber: string,
    limit = 100,
  ): Promise<Property[]> {
    return this.request<Property[]>(
      `/properties/by-phone/${encodeURIComponent(phoneNumber)}?limit=${limit}`,
    );
  }

  // System methods
  async getScraperInfo(): Promise<ScraperInfo> {
    return this.request<ScraperInfo>("/scraper/scraper-info");
  }

  async getHealth(): Promise<{ status: string; timestamp: string }> {
    return this.request<{ status: string; timestamp: string }>("/health");
  }

  async testScraper(params: {
    platform: string;
    transaction_type?: string;
    places?: string[];
    categories?: string[];
    max_properties?: number;
  }): Promise<{
    success: boolean;
    platform: string;
    transaction_type: string;
    total_properties: number;
    details_fetched: number;
    details_filtered: number;
    properties: {
      platform: string;
      listing_url: string;
      contact_name: string;
      phone_number: string;
      transaction_type: string;
      categories: string[];
      additional_data: Record<string, unknown>;
    }[];
    scraping_stats?: {
      details_fetched: number;
      details_filtered: number;
      listings_found: number;
      listing_pages_crawled: number;
      failures: number;
      duration_seconds: number;
      estimated_cost_usd: number;
    };
    message: string;
  }> {
    return this.request("/scraper/test-scraper", {
      method: "POST",
      body: JSON.stringify({
        platform: params.platform,
        transaction_type: params.transaction_type || "rent",
        places: params.places || ["zurich"],
        categories: params.categories || ["apartment"],
        max_properties: params.max_properties || 3,
      }),
    });
  }

  async getCacheStats(): Promise<{
    success: boolean;
    total_entries: number;
    total_memory_bytes: number;
    total_memory_mb: number;
    avg_ttl_seconds: number;
    min_ttl_seconds: number;
    max_ttl_seconds: number;
    error?: string;
  }> {
    return this.request("/scraper/cache-stats");
  }

  // Category methods
  async getCategories(params?: {
    active_only?: boolean;
    transaction_type?: string;
  }): Promise<Category[]> {
    const searchParams = new URLSearchParams();
    if (params?.active_only !== undefined) {
      searchParams.append("active_only", params.active_only.toString());
    }
    if (params?.transaction_type) {
      searchParams.append("transaction_type", params.transaction_type);
    }

    const endpoint = `/categories/${
      searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`;
    return this.request(endpoint);
  }

  async getCategoriesSummary(params?: {
    active_only?: boolean;
    transaction_type?: string;
  }): Promise<CategorySummary[]> {
    const searchParams = new URLSearchParams();
    if (params?.active_only !== undefined) {
      searchParams.append("active_only", params.active_only.toString());
    }
    if (params?.transaction_type) {
      searchParams.append("transaction_type", params.transaction_type);
    }

    const endpoint = `/categories/summary${
      searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`;
    return this.request(endpoint);
  }

  async getCategoriesByTransactionType(
    transactionType: string,
    activeOnly = true,
  ): Promise<CategorySummary[]> {
    const searchParams = new URLSearchParams();
    searchParams.append("active_only", activeOnly.toString());

    return this.request(
      `/categories/by-transaction-type/${transactionType}?${searchParams.toString()}`,
    );
  }

  async getCategory(id: string): Promise<Category> {
    return this.request(`/categories/${id}`);
  }

  async createCategory(data: {
    value: string;
    display_name: string;
    icon?: string;
    description?: string;
    active?: boolean;
    transaction_types: string[];
  }): Promise<Category> {
    return this.request("/categories/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateCategory(
    id: string,
    data: {
      value?: string;
      display_name?: string;
      icon?: string;
      description?: string;
      active?: boolean;
      transaction_types?: string[];
    },
  ): Promise<Category> {
    return this.request(`/categories/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: string): Promise<{ message: string }> {
    return this.request(`/categories/${id}`, {
      method: "DELETE",
    });
  }

  async activateCategory(id: string): Promise<{ message: string }> {
    return this.request(`/categories/${id}/activate`, {
      method: "POST",
    });
  }

  // Blacklist methods
  async getBlacklists(params?: {
    type?: string;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<BlacklistItem[]> {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.append("type", params.type);
    if (params?.search) searchParams.append("search", params.search);
    if (params?.skip) searchParams.append("skip", params.skip.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());

    const queryString = searchParams.toString();
    const endpoint = `/blacklists/${queryString ? `?${queryString}` : ""}`;
    return this.request<BlacklistItem[]>(endpoint);
  }

  async getBlacklist(id: string): Promise<BlacklistItem> {
    return this.request<BlacklistItem>(`/blacklists/${id}`);
  }

  async createBlacklistItem(data: {
    type: string;
    value: string;
    description?: string;
    source?: string;
  }): Promise<BlacklistItem> {
    return this.request<BlacklistItem>("/blacklists/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateBlacklist(
    id: string,
    data: { description?: string },
  ): Promise<BlacklistItem> {
    return this.request<BlacklistItem>(`/blacklists/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteBlacklist(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/blacklists/${id}`, {
      method: "DELETE",
    });
  }

  async deleteBlacklistsBulk(params?: {
    type?: string;
  }): Promise<{ message: string; deleted_count: number }> {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.append("type", params.type);

    const queryString = searchParams.toString();
    const endpoint = `/blacklists/${queryString ? `?${queryString}` : ""}`;
    return this.request<{ message: string; deleted_count: number }>(endpoint, {
      method: "DELETE",
    });
  }

  async getBlacklistStats(): Promise<{
    total_phone_numbers: number;
    total_legal_names: number;
    total_keywords: number;
    total_items: number;
  }> {
    return this.request(`/blacklists/stats`);
  }

  async checkPhoneBlacklist(phoneNumber: string): Promise<{
    phone_number: string;
    is_blacklisted: boolean;
    blacklist_item?: BlacklistItem;
  }> {
    return this.request<{
      phone_number: string;
      is_blacklisted: boolean;
      blacklist_item?: BlacklistItem;
    }>(`/blacklists/check/phone/${encodeURIComponent(phoneNumber)}`);
  }

  async checkLegalNameBlacklist(legalName: string): Promise<{
    legal_name: string;
    is_blacklisted: boolean;
    blacklist_item?: BlacklistItem;
  }> {
    return this.request<{
      legal_name: string;
      is_blacklisted: boolean;
      blacklist_item?: BlacklistItem;
    }>(`/blacklists/check/legal-name/${encodeURIComponent(legalName)}`);
  }

  async checkKeywordsBlacklist(params: {
    title?: string;
    description?: string;
  }): Promise<{
    title?: string;
    description?: string;
    has_blacklisted_keywords: boolean;
    found_keywords: string[];
  }> {
    const searchParams = new URLSearchParams();
    if (params.title) searchParams.append("title", params.title);
    if (params.description)
      searchParams.append("description", params.description);

    const queryString = searchParams.toString();
    const endpoint = `/blacklists/check/keywords/${
      queryString ? `?${queryString}` : ""
    }`;
    return this.request<{
      title?: string;
      description?: string;
      has_blacklisted_keywords: boolean;
      found_keywords: string[];
    }>(endpoint);
  }

  // Employee methods
  async getEmployees(params?: {
    skip?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
    function?: string;
    is_active?: boolean;
  }): Promise<EmployeeListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.skip !== undefined)
      searchParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      searchParams.append("limit", params.limit.toString());
    if (params?.search) searchParams.append("search", params.search);
    if (params?.role) searchParams.append("role", params.role);
    if (params?.status) searchParams.append("status", params.status);
    if (params?.function) searchParams.append("function", params.function);
    if (params?.is_active !== undefined)
      searchParams.append("is_active", params.is_active.toString());

    const query = searchParams.toString();
    return this.request<EmployeeListResponse>(
      `/employees/${query ? `?${query}` : ""}`,
    );
  }

  async getEmployee(employeeId: string): Promise<Employee> {
    return this.request<Employee>(`/employees/${employeeId}`);
  }

  async createEmployee(
    employeeData: EmployeeCreate,
  ): Promise<CreateEmployeeResponse> {
    return this.request<CreateEmployeeResponse>("/employees/", {
      method: "POST",
      body: JSON.stringify(employeeData),
    });
  }

  async updateEmployee(
    employeeId: string,
    employeeData: EmployeeUpdate,
  ): Promise<Employee> {
    return this.request<Employee>(`/employees/${employeeId}`, {
      method: "PUT",
      body: JSON.stringify(employeeData),
    });
  }

  async deleteEmployee(employeeId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/employees/${employeeId}`, {
      method: "DELETE",
    });
  }

  async resetEmployeePassword(employeeId: string): Promise<{
    message: string;
    temporary_password: string;
    employee: {
      id: string;
      full_name: string;
      email: string;
    };
  }> {
    return this.request(`/employees/${employeeId}/reset-password`, {
      method: "POST",
    });
  }

  async getEmployeeStats(): Promise<EmployeeStats> {
    return this.request<EmployeeStats>("/employees/stats/summary");
  }

  async getEmployeeProperties(
    employeeId: string,
    params?: {
      skip?: number;
      limit?: number;
      name?: string;
      transaction_type?: string;
      categories?: string[];
      states?: string[];
      run_ids?: string[];
      lifecycle?: "active" | "inactive" | "all";
      search_q?: string;
      locality?: string;
      postal_code?: string;
      street?: string;
      priceMin?: string;
      priceMax?: string;
      sortBy?: string;
      sortOrder?: string;
    },
  ): Promise<PaginatedPropertyResponse> {
    const searchParams = new URLSearchParams();
    const sq = params?.search_q?.trim();
    if (sq && sq.length >= 2) searchParams.append("search_q", sq);
    if (params?.skip !== undefined)
      searchParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      searchParams.append("limit", params.limit.toString());
    if (params?.name) searchParams.append("name", params.name);
    if (params?.transaction_type)
      searchParams.append("transaction_type", params.transaction_type);
    if (params?.categories && params.categories.length > 0) {
      params.categories.forEach((category) => {
        searchParams.append("categories", category);
      });
    }
    if (params?.states && params.states.length > 0) {
      params.states.forEach((state) => {
        searchParams.append("states", state);
      });
    }
    if (params?.run_ids && params.run_ids.length > 0) {
      params.run_ids.forEach((id) => {
        searchParams.append("run_ids", id);
      });
    }
    if (params?.lifecycle) searchParams.append("lifecycle", params.lifecycle);
    if (params?.locality) searchParams.append("locality", params.locality);
    if (params?.postal_code)
      searchParams.append("postal_code", params.postal_code);
    if (params?.street) searchParams.append("address_q", params.street);
    if (params?.priceMin && params.priceMin != "0")
      searchParams.append("price_min", params.priceMin);
    if (params?.priceMax && params.priceMax != "0")
      searchParams.append("price_max", params.priceMax);
    if (params?.sortBy) searchParams.append("sort_by", params.sortBy);
    if (params?.sortOrder) searchParams.append("sort_order", params.sortOrder);

    const query = searchParams.toString();
    return this.request<PaginatedPropertyResponse>(
      `/employees/${employeeId}/properties${query ? `?${query}` : ""}`,
    );
  }

  async getMyPropertyCategories(employeeId: string): Promise<string[]> {
    return this.request<string[]>(
      `/employees/${employeeId}/properties/categories`,
    );
  }

  async getEmployeePropertiesCount(
    employeeId: string,
  ): Promise<{ count: number }> {
    return this.request<{ count: number }>(
      `/employees/${employeeId}/properties/count`,
    );
  }

  // Auth methods
  async login(
    email: string,
    password: string,
  ): Promise<{
    access_token: string;
    refresh_token: string;
    token_type: string;
    user: {
      email: string;
      full_name: string;
      role: "super_admin" | "admin" | "manager" | "employee";
      id: string;
      is_super_admin: boolean;
      must_change_password?: boolean;
    };
  }> {
    return this.request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  async getCurrentUser(): Promise<{
    email: string;
    full_name: string;
    role: "super_admin" | "admin" | "manager" | "employee";
    id: string;
    is_super_admin: boolean;
    must_change_password: boolean;
  }> {
    return this.request("/auth/me");
  }

  async logout(): Promise<{ message: string }> {
    return this.request("/auth/logout", {
      method: "POST",
    });
  }

  async changePassword(data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }): Promise<{ message: string }> {
    return this.request("/auth/change-password", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  post<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Property Notes methods
  async getPropertyNotes(
    propertyId: string,
    skip = 0,
    limit = 100,
  ): Promise<PropertyNote[]> {
    return this.request<PropertyNote[]>(
      `/property-notes/property/${propertyId}/notes?skip=${skip}&limit=${limit}`,
    );
  }

  async createPropertyNote(
    propertyId: string,
    noteData: PropertyNoteCreate,
  ): Promise<PropertyNote> {
    return this.request<PropertyNote>(
      `/property-notes/property/${propertyId}/notes`,
      {
        method: "POST",
        body: JSON.stringify(noteData),
      },
    );
  }

  async getPropertyNote(noteId: string): Promise<PropertyNote> {
    return this.request<PropertyNote>(`/property-notes/notes/${noteId}`);
  }

  async updatePropertyNote(
    noteId: string,
    noteData: PropertyNoteUpdate,
  ): Promise<PropertyNote> {
    return this.request<PropertyNote>(`/property-notes/notes/${noteId}`, {
      method: "PUT",
      body: JSON.stringify(noteData),
    });
  }

  async deletePropertyNote(noteId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/property-notes/notes/${noteId}`,
      {
        method: "DELETE",
      },
    );
  }

  // =============================================================================
  // Documents
  // =============================================================================

  async sendDocuments(request: SendRequest): Promise<SendResponse> {
    return this.request<SendResponse>("/documents/send", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async getDocumentCatalog(): Promise<CatalogResponse> {
    return this.request<CatalogResponse>("/documents/catalog");
  }

  async sendBundle(
    bundleName: string,
    data: Record<string, unknown>,
    recipientEmail: string,
  ): Promise<SendResponse> {
    return this.sendDocuments({
      type: "bundle",
      bundle_name: bundleName,
      data,
      recipients: { to: recipientEmail },
    });
  }

  // =============================================================================
  // Uploaded Documents (Multi-Language)
  // =============================================================================

  async getUploadedDocuments(
    skip = 0,
    limit = 100,
  ): Promise<DocumentListResponse> {
    return this.request<DocumentListResponse>(
      `/uploaded-documents?skip=${skip}&limit=${limit}`,
    );
  }

  async getUploadedDocument(id: string): Promise<UploadedDocument> {
    return this.request<UploadedDocument>(`/uploaded-documents/${id}`);
  }

  async createUploadedDocument(
    data: DocumentCreate,
  ): Promise<UploadedDocument> {
    return this.request<UploadedDocument>("/uploaded-documents", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateUploadedDocument(
    id: string,
    data: DocumentUpdate,
  ): Promise<UploadedDocument> {
    return this.request<UploadedDocument>(`/uploaded-documents/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteUploadedDocument(id: string): Promise<void> {
    const authToken = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/uploaded-documents/${id}`, {
      method: "DELETE",
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    });
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ detail: "Delete failed" }));
      throw new Error(err.detail || "Delete failed");
    }
  }

  async updateFieldConfig(
    documentId: string,
    fields: FieldConfig[],
  ): Promise<UploadedDocument> {
    return this.request<UploadedDocument>(
      `/uploaded-documents/${documentId}/fields`,
      {
        method: "PUT",
        body: JSON.stringify({ fields }),
      },
    );
  }

  // =============================================================================
  // Document Bundles
  // =============================================================================

  async getDocumentBundles(): Promise<BundleListResponse> {
    return this.request<BundleListResponse>("/uploaded-documents/bundles");
  }

  async createDocumentBundle(
    data: BundleCreate,
  ): Promise<DocumentBundleResponse> {
    return this.request<DocumentBundleResponse>("/uploaded-documents/bundles", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateDocumentBundle(
    id: string,
    data: BundleUpdate,
  ): Promise<DocumentBundleResponse> {
    return this.request<DocumentBundleResponse>(
      `/uploaded-documents/bundles/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
  }

  async deleteDocumentBundle(id: string): Promise<void> {
    const authToken = getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/uploaded-documents/bundles/${id}`,
      {
        method: "DELETE",
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      },
    );
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ detail: "Delete failed" }));
      throw new Error(err.detail || "Delete failed");
    }
  }

  // =============================================================================
  // Email Templates
  // =============================================================================

  async getEmailTemplates(): Promise<EmailTemplateListResponse> {
    return this.request<EmailTemplateListResponse>(
      "/uploaded-documents/email-templates",
    );
  }

  async createEmailTemplate(
    data: EmailTemplateCreate,
  ): Promise<CustomEmailTemplateResponse> {
    return this.request<CustomEmailTemplateResponse>(
      "/uploaded-documents/email-templates",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  }

  async updateEmailTemplate(
    id: string,
    data: EmailTemplateUpdate,
  ): Promise<CustomEmailTemplateResponse> {
    return this.request<CustomEmailTemplateResponse>(
      `/uploaded-documents/email-templates/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
  }

  async deleteEmailTemplate(id: string): Promise<void> {
    const authToken = getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/uploaded-documents/email-templates/${id}`,
      {
        method: "DELETE",
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      },
    );
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ detail: "Delete failed" }));
      throw new Error(err.detail || "Delete failed");
    }
  }

  async uploadDocumentVariant(
    documentId: string,
    language: string,
    file: File,
  ): Promise<DocumentVariant> {
    const formData = new FormData();
    formData.append("file", file);
    const authToken = getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/uploaded-documents/${documentId}/variants/${language}`,
      {
        method: "POST",
        body: formData,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      },
    );
    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: "Upload failed" }));
      throw new Error(error.detail || "Upload failed");
    }
    return response.json();
  }

  async deleteDocumentVariant(
    documentId: string,
    language: string,
  ): Promise<void> {
    const authToken = getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/uploaded-documents/${documentId}/variants/${language}`,
      {
        method: "DELETE",
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      },
    );
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ detail: "Delete failed" }));
      throw new Error(err.detail || "Delete failed");
    }
  }

  async downloadDocumentVariant(
    documentId: string,
    language: string,
  ): Promise<Blob> {
    const authToken = getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/uploaded-documents/${documentId}/variants/${language}/download`,
      {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      },
    );
    if (!response.ok) throw new Error("Download failed");
    return response.blob();
  }

  async getPendingProperties(): Promise<PropertiesWithCountResponse> {
    return this.request<PropertiesWithCountResponse>(
      "/properties/state/pending",
    );
  }

  async setPropertyStateNotReached(propertyId: string) {
    return this.request(`/properties/${propertyId}/state/not-reached`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async setPropertyStateNoInterest(
    propertyId: string,
    data: {
      reason: string;
      future_collaboration?: boolean;
    },
  ) {
    return this.request(`/properties/${propertyId}/state/no-interest`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async setPropertyStateContactLater(
    propertyId: string,
    data: {
      contact_date?: string;
      contact_reason?: string;
    },
  ) {
    return this.request(`/properties/${propertyId}/state/contact-later`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async setPropertyStatePending(
    propertyId: string,
    data: {
      pending_reason: string;
      service_type: string;
      follow_up_date?: string;
      time?: string;
      pending_notes?: string;
      assigned_agent_id?: string;
    },
  ) {
    return this.request(`/properties/${propertyId}/state/pending`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async setPropertyStateOpportunity(
    propertyId: string,
    data: {
      opportunity_service: string;
      opportunity_description?: string;
      opportunity_date?: string;
    },
  ) {
    return this.request(`/properties/${propertyId}/state/opportunity`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async setPropertyStateClosed(
    propertyId: string,
    data: {
      turnover?: number;
      revenue?: number;
      completion_date?: string;
      service_type?: string;
      costs?: { description: string; amount: number }[];
      commissions?: {
        employee_id: string;
        commission_percentage?: number;
        commission_amount?: number;
        commission_basis?: "gross" | "net";
      }[];
    },
  ) {
    return this.request(`/properties/${propertyId}/state/closed`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getPropertyStateHistory(
    propertyId: string,
    limit = 50,
  ): Promise<StateHistoryEntry[]> {
    return this.request<StateHistoryEntry[]>(
      `/properties/${propertyId}/state-history?limit=${limit}`,
    );
  }

  async updateStateHistoryEntry(
    propertyId: string,
    historyId: string,
    data: Record<string, unknown>,
  ): Promise<StateHistoryEntry> {
    return this.request<StateHistoryEntry>(
      `/properties/${propertyId}/state-history/${historyId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
  }

  async toggleCommissionPaid(
    propertyId: string,
    commissionId: string,
    isPaid: boolean,
  ): Promise<unknown> {
    return this.request(
      `/properties/${propertyId}/commissions/${commissionId}/paid`,
      {
        method: "PATCH",
        body: JSON.stringify({ is_paid: isPaid }),
      },
    );
  }

  async getRevenueStats(params?: {
    start_date?: string;
    end_date?: string;
    employee_id?: string;
    period?: "current_month" | "current_year" | "current_quarter";
    group_by?: "day" | "week" | "month";
    service_type?: string;
  }): Promise<RevenueStatsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);
    if (params?.employee_id)
      searchParams.append("employee_id", params.employee_id);
    if (params?.period) searchParams.append("period", params.period);
    if (params?.group_by) searchParams.append("group_by", params.group_by);
    if (params?.service_type)
      searchParams.append("service_type", params.service_type);

    return this.request(`/revenue/stats?${searchParams.toString()}`);
  }

  async getNoInterestStats(params?: {
    start_date?: string;
    end_date?: string;
    employee_id?: string;
    service_type?: string;
  }): Promise<NoInterestStatsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);
    if (params?.employee_id)
      searchParams.append("employee_id", params.employee_id);
    if (params?.service_type)
      searchParams.append("service_type", params.service_type);
    const qs = searchParams.toString();
    return this.request<NoInterestStatsResponse>(
      `/properties/state/no-interest/stats${qs ? `?${qs}` : ""}`,
    );
  }

  async getMyRevenueStats(params?: {
    start_date?: string;
    end_date?: string;
    period?: "current_month" | "current_year" | "current_quarter";
    group_by?: "day" | "week" | "month";
    service_type?: string;
  }): Promise<RevenueStatsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);
    if (params?.period) searchParams.append("period", params.period);
    if (params?.group_by) searchParams.append("group_by", params.group_by);
    if (params?.service_type)
      searchParams.append("service_type", params.service_type);

    return this.request(`/revenue/my-stats?${searchParams.toString()}`);
  }

  async getStatisticsOverview(params?: {
    start_date?: string;
    end_date?: string;
    employee_id?: string;
    group_by?: string;
    period?: string;
    service_type?: string;
  }): Promise<StatisticResponse> {
    const searchParams = new URLSearchParams();

    if (params?.start_date)
      searchParams.append("start_date", params.start_date);
    if (params?.end_date) searchParams.append("end_date", params.end_date);
    if (params?.employee_id)
      searchParams.append("employee_id", params.employee_id);
    if (params?.group_by) searchParams.append("group_by", params.group_by);
    if (params?.period) searchParams.append("period", params.period);
    if (params?.service_type)
      searchParams.append("service_type", params.service_type);

    return this.request<StatisticResponse>(
      `/statistics/?${searchParams.toString()}`,
      {
        method: "GET",
      },
    );
  }

  async exportSalesDashboard(params: {
    columns: string[];
    start_date?: string;
    end_date?: string;
    employee_id?: string;
    service_type?: string;
    exporter_name: string;
  }): Promise<Blob> {
    return this.requestBlob("/properties/export", {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  // Employee Goals methods

  async createGoal(data: GoalCreate): Promise<EmployeeGoal> {
    return this.request<EmployeeGoal>("/employee-goals/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getGoals(params?: {
    employee_id?: string;
    status?: string;
    skip?: number;
    limit?: number;
  }): Promise<GoalListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.employee_id)
      searchParams.append("employee_id", params.employee_id);
    if (params?.status) searchParams.append("status", params.status);
    if (params?.skip !== undefined)
      searchParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined)
      searchParams.append("limit", params.limit.toString());
    const query = searchParams.toString();
    return this.request<GoalListResponse>(
      `/employee-goals/${query ? `?${query}` : ""}`,
    );
  }

  async getGoal(goalId: string): Promise<EmployeeGoal> {
    return this.request<EmployeeGoal>(`/employee-goals/${goalId}`);
  }

  async updateGoal(goalId: string, data: GoalUpdate): Promise<EmployeeGoal> {
    return this.request<EmployeeGoal>(`/employee-goals/${goalId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteGoal(goalId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/employee-goals/${goalId}`, {
      method: "DELETE",
    });
  }

  async getGoalProgress(goalId: string): Promise<GoalProgressResponse> {
    return this.request<GoalProgressResponse>(
      `/employee-goals/${goalId}/progress`,
    );
  }

  async getEmployeeGoalsSummary(employeeId: string): Promise<GoalSummary> {
    return this.request<GoalSummary>(
      `/employee-goals/employee/${employeeId}/summary`,
    );
  }

  async getGoalsDashboard(): Promise<GoalDashboardResponse> {
    return this.request<GoalDashboardResponse>("/employee-goals/dashboard");
  }

  // DEPRECATED methods

  async updatePropertyState(id: string, state: string): Promise<Property> {
    return this.request<Property>(`/properties/${id}/state`, {
      method: "PUT",
      body: JSON.stringify({ state }),
    });
  }
}

export const apiClient = new ApiClient();
export const api = apiClient;
